import { upload as tigrisStorageUpload } from '@tigrisdata/storage/client'
import { useRef, useState } from 'react'
import { fetcher } from '#app/utils/fetcher.ts'

type AllocateResponse = {
	fileId: string
	originalKey: string
	uploadServiceUrl: string
	originalUploadMode?: 'direct' | 'proxy'
	sourceKey?: string
}

/** Browser → app bridge → Tigris presigned PUT/multipart (progress via SDK). */
const TIGRIS_CLIENT_UPLOAD_URL = '/api/storage/tigris-client-upload'

const MIB = 1024 * 1024
/** Show “keep tab open” and similar when upload is this large or bigger. */
const LARGE_UPLOAD_BYTES = 100 * MIB

function formatMegabytes(bytes: number): string {
	if (bytes >= 100 * MIB) return Math.round(bytes / MIB).toString()
	return (bytes / MIB).toFixed(1)
}

/**
 * Extracts a human-readable message from an error response body, trying JSON
 * `message`/`error` keys (the two shapes used across our own routes and the
 * FastAPI audio service respectively) before falling back to the raw text.
 */
function extractErrorMessage(text: string, fallback: string): string {
	try {
		const parsed = JSON.parse(text) as { message?: string; error?: string }
		if (typeof parsed.message === 'string' && parsed.message) return parsed.message
		if (typeof parsed.error === 'string' && parsed.error) return parsed.error
	} catch {
		// not JSON, fall through
	}
	return text || fallback
}

function formatEtaSeconds(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return ''
	if (seconds < 45) return `~${Math.max(1, Math.ceil(seconds))} sec left`
	if (seconds < 3600) return `~${Math.max(1, Math.ceil(seconds / 60))} min left`
	const h = seconds / 3600
	return `~${h < 10 ? h.toFixed(1) : Math.round(h)} hr left`
}

/** Larger files: fewer round-trips + more parallel parts (SDK defaults: 5 MiB, concurrency 4). */
function getTigrisMultipartOptions(size: number): {
	multipart: boolean
	partSize?: number
	concurrency?: number
} {
	if (size <= 5 * MIB) return { multipart: false }
	if (size <= 100 * MIB) return { multipart: true }
	if (size <= 200 * MIB) {
		return { multipart: true, partSize: 16 * MIB, concurrency: 6 }
	}
	return { multipart: true, partSize: 32 * MIB, concurrency: 8 }
}

/**
 * Upload original audio: direct PUT to Tigris (with progress), then FFmpeg optimize
 * on the audio service by object key; or legacy proxy upload to /optimize-upload.
 */
export function useAudioUpload() {
	const [progress, setProgress] = useState(0)
	const [statusMessage, setStatusMessage] = useState<string | null>(null)
	const [largeUploadInProgress, setLargeUploadInProgress] = useState(false)
	const uploadRateRef = useRef({ loaded: 0, t: 0 })
	const lastEtaRef = useRef<string | null>(null)
	const lastEtaAtRef = useRef(0)
	const lastStatusUpdateAtRef = useRef(0)

	function isTransientFetchError(error: unknown) {
		const message = error instanceof Error ? error.message.toLowerCase() : ''
		return (
			message.includes('fetch failed') ||
			message.includes('failed to fetch') ||
			message.includes('networkerror') ||
			message.includes('network error') ||
			message.includes('aborted') ||
			message.includes('timeout')
		)
	}

	function normalizeUploadError(error: unknown) {
		if (!(error instanceof Error)) {
			return 'Upload failed. Please try again in a moment.'
		}
		const message = error.message.toLowerCase()
		if (message.includes('aborted') || message.includes('timeout')) {
			return 'Uploading took too long. Please keep this tab open and try again.'
		}
		if (isTransientFetchError(error)) {
			return 'Upload service is temporarily unavailable. Please try again in a moment.'
		}
		return error.message || 'Upload failed. Please try again in a moment.'
	}

	async function pollUntilUploaded(fileId: string, options?: { floorProgress?: number }) {
		const floor = options?.floorProgress ?? 0

		async function pollOnce() {
			const res = await fetcher(`/api/audio/status/${fileId}`, {
				credentials: 'include',
				retry: 1,
				timeoutMs: 15000,
			})
			if (!res.ok) return null
			const data = (await res.json()) as {
				status: string
				progress?: number
				display_status?: string | null
			}
			if (typeof data.progress === 'number') {
				const shown = Math.max(floor, Math.round(data.progress))
				setProgress(shown)
			}
			if (typeof data.display_status === 'string') {
				setStatusMessage(data.display_status)
			}
			return data
		}

		let attempts = 0
		const maxAttempts = 300
		let transientPollErrors = 0
		const maxTransientPollErrors = 8
		let last: { status: string } | null = null
		while (attempts < maxAttempts) {
			let data: Awaited<ReturnType<typeof pollOnce>> = null
			try {
				data = await pollOnce()
				transientPollErrors = 0
			} catch (error) {
				if (isTransientFetchError(error)) {
					transientPollErrors += 1
					setStatusMessage('Optimizing your file (retrying...)')
					if (transientPollErrors >= maxTransientPollErrors) {
						throw new Error(
							'Upload service is temporarily unavailable. Please try again in a moment.',
						)
					}
				} else {
					throw error
				}
			}
			if (data) {
				last = data
				if (data.status === 'uploaded') {
					setProgress(100)
					setStatusMessage('Upload complete')
					break
				}
				const progressComplete =
					typeof data.progress === 'number' && data.progress >= 99
				const messageComplete =
					typeof data.display_status === 'string' &&
					data.display_status.trim().toLowerCase().includes('upload complete')
				if (progressComplete && messageComplete) {
					setProgress(100)
					setStatusMessage('Upload complete')
					last = { ...data, status: 'uploaded' }
					break
				}
				if (data.status === 'error') {
					throw new Error('Upload failed on server')
				}
			}
			await new Promise((r) => setTimeout(r, 2000))
			attempts += 1
		}

		if (!last || last.status !== 'uploaded') {
			setStatusMessage('Upload timed out')
			throw new Error(
				'Upload is taking longer than expected. Please try again in a moment.',
			)
		}
	}

	async function uploadDirectToStorageThenOptimize(
		file: File,
		allocateData: AllocateResponse,
	) {
		const { fileId, sourceKey } = allocateData
		if (!sourceKey) {
			throw new Error('Invalid response: missing sourceKey for direct upload')
		}

		setStatusMessage('Preparing to send your file')
		const contentType = file.type?.trim() || 'application/octet-stream'
		const mp = getTigrisMultipartOptions(file.size)
		uploadRateRef.current = { loaded: 0, t: 0 }

		try {
			const uploadResult = await tigrisStorageUpload(sourceKey, file, {
				url: TIGRIS_CLIENT_UPLOAD_URL,
				contentType,
				multipart: mp.multipart,
				...(mp.partSize !== undefined ? { partSize: mp.partSize } : {}),
				...(mp.concurrency !== undefined ? { concurrency: mp.concurrency } : {}),
				// @tigrisdata/storage/client reports XHR upload progress (singlepart) or aggregated part progress (multipart).
				onUploadProgress: ({ loaded, total, percentage }) => {
					const uploadPct =
						total > 0
							? Math.min(100, Math.round((loaded / total) * 100))
							: Math.min(100, Math.round(percentage))
					// Reserve ~15% of the bar for FFmpeg / server-side optimize + poll.
					setProgress(Math.min(85, Math.round((uploadPct / 100) * 85)))

					const now = performance.now()
					const prev = uploadRateRef.current
					let nextEta: string | null = null
					if (total > 0 && prev.t > 0 && loaded > prev.loaded) {
						const dt = (now - prev.t) / 1000
						if (dt >= 0.25) {
							const rate = (loaded - prev.loaded) / dt
							const remaining = total - loaded
							if (rate > 512 && remaining > 0) {
								const eta = formatEtaSeconds(remaining / rate)
								if (eta) nextEta = eta
							}
						}
					}
					uploadRateRef.current = { loaded, t: now }
					if (nextEta) {
						lastEtaRef.current = nextEta
						lastEtaAtRef.current = now
					}
					const stickyEta =
						lastEtaRef.current && now - lastEtaAtRef.current < 5000
							? ` · ${lastEtaRef.current}`
							: ''

					const mbLine =
						total > 0
							? `${formatMegabytes(loaded)} / ${formatMegabytes(total)} MB`
							: ''
					const pctPart = `${uploadPct}%`
					// Keep text updates slower than progress updates to avoid flicker.
					if (
						now - lastStatusUpdateAtRef.current >= 800 ||
						uploadPct === 100 ||
						uploadPct % 5 === 0
					) {
						lastStatusUpdateAtRef.current = now
						setStatusMessage(
							mbLine
								? `Sending your file — ${mbLine} (${pctPart})${stickyEta}`
								: `Sending your file (${pctPart})${stickyEta}`,
						)
					}
				},
			})
			if (uploadResult.error) {
				throw uploadResult.error
			}
		} catch (error) {
			throw new Error(normalizeUploadError(error))
		}

		setProgress(88)
		setStatusMessage('Optimizing your file')

		let startRes: Response
		try {
			startRes = await fetcher('/api/audio/start-optimize-from-key', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ fileId }),
				retry: 2,
				timeoutMs: 90_000,
			})
		} catch (error) {
			throw new Error(normalizeUploadError(error))
		}

		if (!startRes.ok) {
			const text = await startRes.text().catch(() => '')
			throw new Error(extractErrorMessage(text, 'Could not start optimization.'))
		}

		await pollUntilUploaded(fileId, { floorProgress: 88 })
	}

	async function uploadViaProxyOptimize(
		file: File,
		allocateData: AllocateResponse,
	) {
		const { fileId, originalKey, uploadServiceUrl } = allocateData

		const formData = new FormData()
		formData.append('file_id', fileId)
		formData.append('original_key', originalKey)
		formData.append('file', file, file.name)

		let optimizeRes: Response
		try {
			optimizeRes = await fetcher(`${uploadServiceUrl}/optimize-upload`, {
				method: 'POST',
				body: formData,
				retry: 1,
				timeoutMs: 10 * 60 * 1000,
			})
		} catch (error) {
			throw new Error(normalizeUploadError(error))
		}
		if (!optimizeRes.ok) {
			const text = await optimizeRes.text().catch(() => '')
			throw new Error(
				extractErrorMessage(
					text,
					'Could not start optimization. Please try again in a moment.',
				),
			)
		}

		await pollUntilUploaded(fileId)
	}

	async function uploadFile(file: File): Promise<{ fileId: string }> {
		setProgress(0)
		setStatusMessage(null)
		const isLongSession = file.size >= LARGE_UPLOAD_BYTES
		setLargeUploadInProgress(isLongSession)

		try {
			setStatusMessage('Preparing upload')

			const allocateRes = await fetcher('/api/audio/new-file', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ filename: file.name }),
			})

			const contentTypeHeader = allocateRes.headers.get('content-type') ?? ''

			if (allocateRes.status === 401 || allocateRes.status === 403) {
				throw new Error(
					'Your session is invalid. Please refresh the page and try again.',
				)
			}

			if (!allocateRes.ok) {
				const text = await allocateRes.text().catch(() => '')
				if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
					throw new Error(
						'Server returned an unexpected response while allocating the upload.',
					)
				}
				throw new Error(extractErrorMessage(text, 'Failed to allocate file.'))
			}

			if (!contentTypeHeader.includes('application/json')) {
				throw new Error(
					'Server returned an unexpected response while allocating the upload.',
				)
			}

			const allocateData = (await allocateRes.json()) as AllocateResponse
			if (
				!allocateData ||
				typeof allocateData.fileId !== 'string' ||
				typeof allocateData.originalKey !== 'string' ||
				typeof allocateData.uploadServiceUrl !== 'string'
			) {
				throw new Error('Invalid response from allocation endpoint')
			}

			const mode = allocateData.originalUploadMode ?? 'direct'
			if (mode === 'proxy') {
				await uploadViaProxyOptimize(file, allocateData)
			} else {
				await uploadDirectToStorageThenOptimize(file, allocateData)
			}

			return { fileId: allocateData.fileId }
		} finally {
			setLargeUploadInProgress(false)
		}
	}

	return { upload: uploadFile, progress, statusMessage, largeUploadInProgress }
}
